import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Modal from '../common/Modal';
import { useNotification } from '../../contexts/NotificationContext';
import { propertyService, mediaBatchService } from '../../services/api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  batchId: number;
  selectedImageIds: number[]; // vazio = todas
  totalImages: number;
  onAttached?: () => void;
}

interface PropertyOption {
  id: string;
  title: string;
  location?: string;
  cover_image?: string;
}

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1.5px solid ${({ $active, theme }) => ($active ? theme.colors.primary : '#e2e8f0')};
  background: ${({ $active, theme }) => ($active ? `${theme.colors.primary}12` : 'white')};
  color: ${({ $active }) => ($active ? '#1e293b' : '#64748b')};
  font-weight: 600;
  font-size: 0.92rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const List = styled.div`
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
`;

const Item = styled.div<{ $selected: boolean }>`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  background: ${({ $selected, theme }) => ($selected ? `${theme.colors.primary}15` : 'white')};
  border-left: 3px solid
    ${({ $selected, theme }) => ($selected ? theme.colors.primary : 'transparent')};

  &:hover {
    background: #f8fafc;
  }

  strong {
    display: block;
    color: #1e293b;
  }
  small {
    color: #64748b;
  }
`;

const Info = styled.div`
  background: #f1f5f9;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  color: #475569;
  margin-bottom: 1rem;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
  cursor: pointer;
  color: #333;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  border-top: 1px solid #eee;
  padding-top: 1.25rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.7rem 1.5rem;
  border-radius: 6px;
  font-size: 0.95rem;
  cursor: pointer;
  border: none;
  font-weight: 500;
  background: ${({ $variant, theme }) => ($variant === 'secondary' ? '#e2e8f0' : theme.colors.primary)};
  color: ${({ $variant }) => ($variant === 'secondary' ? '#1e293b' : 'white')};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: #94a3b8;
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ModeBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.55rem;
  border-radius: 8px;
  border: 1.5px solid ${({ $active, theme }) => ($active ? theme.colors.primary : '#e2e8f0')};
  background: ${({ $active, theme }) => ($active ? `${theme.colors.primary}12` : 'white')};
  color: #1e293b;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;

  small {
    display: block;
    font-weight: 500;
    color: #64748b;
    font-size: 0.72rem;
    margin-top: 0.15rem;
  }
`;

const Field = styled.div`
  margin-bottom: 0.9rem;

  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 0.35rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.65rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.65rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  background: white;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  resize: vertical;
  font-family: inherit;
`;

const Hint = styled.p`
  font-size: 0.8rem;
  color: #64748b;
  margin: 0.25rem 0 0.9rem;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PROPERTY_TYPES = [
  'apartamento',
  'casa',
  'terreno',
  'comercial',
  'chacara',
  'galpao',
];

type MainTab = 'existing' | 'new';
type CreateMode = 'ai' | 'manual';

const AttachToPropertyModal: React.FC<Props> = ({
  isOpen,
  onClose,
  batchId,
  selectedImageIds,
  totalImages,
  onAttached,
}) => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [tab, setTab] = useState<MainTab>('existing');

  // Aba "imóvel existente"
  const [search, setSearch] = useState('');
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [setCover, setSetCover] = useState(false);
  const [attaching, setAttaching] = useState(false);

  // Aba "criar novo"
  const [createMode, setCreateMode] = useState<CreateMode>('ai');
  const [aiDescription, setAiDescription] = useState('');
  const [mTitle, setMTitle] = useState('');
  const [mType, setMType] = useState('apartamento');
  const [mTransaction, setMTransaction] = useState('venda');
  const [mPrice, setMPrice] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchProperties = useCallback(async (term: string) => {
    try {
      setLoading(true);
      const data = await propertyService.getAll({ search: term || undefined, limit: 50 });
      const list = Array.isArray(data) ? data : data?.items || [];
      setProperties(
        list.map((p: any) => ({
          id: p.id,
          title: p.title,
          location: p.location || p.address?.city,
          cover_image: p.cover_image,
        }))
      );
    } catch (error) {
      showNotification('Erro ao carregar imóveis', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    if (isOpen) {
      setTab('existing');
      setSearch('');
      setSelectedProperty(null);
      setSetCover(false);
      setCreateMode('ai');
      setAiDescription('');
      setMTitle('');
      setMType('apartamento');
      setMTransaction('venda');
      setMPrice('');
      fetchProperties('');
    }
  }, [isOpen, fetchProperties]);

  useEffect(() => {
    if (!isOpen || tab !== 'existing') return;
    const t = setTimeout(() => fetchProperties(search), 350);
    return () => clearTimeout(t);
  }, [search, isOpen, tab, fetchProperties]);

  const count = selectedImageIds.length || totalImages;

  const handleAttach = async () => {
    if (!selectedProperty) {
      showNotification('Selecione um imóvel', 'error');
      return;
    }
    try {
      setAttaching(true);
      const result = await mediaBatchService.attachToProperty(batchId, selectedProperty, {
        imageIds: selectedImageIds.length ? selectedImageIds : undefined,
        setCover,
      });
      showNotification(result.message, 'success');
      onAttached?.();
      onClose();
    } catch (error) {
      showNotification('Erro ao anexar imagens ao imóvel', 'error');
    } finally {
      setAttaching(false);
    }
  };

  const handleCreate = async () => {
    if (createMode === 'ai' && !aiDescription.trim()) {
      showNotification('Escreva uma descrição para a IA preencher o imóvel', 'error');
      return;
    }
    if (createMode === 'manual' && !mTitle.trim()) {
      showNotification('Informe pelo menos o título do imóvel', 'error');
      return;
    }
    try {
      setCreating(true);
      const result = await mediaBatchService.createProperty(batchId, {
        imageIds: selectedImageIds.length ? selectedImageIds : undefined,
        useAi: createMode === 'ai',
        description: createMode === 'ai' ? aiDescription.trim() : undefined,
        title: createMode === 'manual' ? mTitle.trim() : undefined,
        type: createMode === 'manual' ? mType : undefined,
        transactionType: createMode === 'manual' ? mTransaction : undefined,
        price: createMode === 'manual' && mPrice ? Number(mPrice) : undefined,
      });
      showNotification(result.message, 'success');
      onAttached?.();
      onClose();
      navigate(`/admin/properties/edit/${result.property_id}`);
    } catch (error: any) {
      showNotification(
        error?.response?.data?.detail || 'Erro ao criar o imóvel a partir das fotos',
        'error'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Usar fotos em um imóvel" size="md">
      <Tabs>
        <Tab $active={tab === 'existing'} onClick={() => setTab('existing')}>
          Imóvel existente
        </Tab>
        <Tab $active={tab === 'new'} onClick={() => setTab('new')}>
          Criar novo imóvel
        </Tab>
      </Tabs>

      <Info>
        {count} foto{count !== 1 ? 's' : ''}{' '}
        {selectedImageIds.length ? 'selecionada(s)' : 'do lote (todas)'} serão usadas
        {selectedImageIds.length
          ? ', na ordem em que você as selecionou.'
          : ', na ordem do lote.'}
        {' '}A sequência pode ser ajustada depois no imóvel.
      </Info>

      {tab === 'existing' ? (
        <>
          <SearchInput
            placeholder="Buscar imóvel por título..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <List>
            {loading ? (
              <Empty>Carregando...</Empty>
            ) : properties.length === 0 ? (
              <Empty>Nenhum imóvel encontrado</Empty>
            ) : (
              properties.map((p) => (
                <Item
                  key={p.id}
                  $selected={selectedProperty === p.id}
                  onClick={() => setSelectedProperty(p.id)}
                >
                  <strong>{p.title}</strong>
                  {p.location && <small>{p.location}</small>}
                </Item>
              ))
            )}
          </List>

          <CheckboxRow>
            <input
              type="checkbox"
              checked={setCover}
              onChange={(e) => setSetCover(e.target.checked)}
            />
            Definir a primeira foto como capa do imóvel
          </CheckboxRow>

          <Actions>
            <Button $variant="secondary" onClick={onClose} disabled={attaching}>
              Cancelar
            </Button>
            <Button onClick={handleAttach} disabled={attaching || !selectedProperty}>
              {attaching ? 'Anexando...' : 'Anexar ao imóvel'}
            </Button>
          </Actions>
        </>
      ) : (
        <>
          <ModeToggle>
            <ModeBtn $active={createMode === 'ai'} onClick={() => setCreateMode('ai')}>
              Preencher com IA
              <small>a partir de um texto</small>
            </ModeBtn>
            <ModeBtn $active={createMode === 'manual'} onClick={() => setCreateMode('manual')}>
              Manual
              <small>dados básicos</small>
            </ModeBtn>
          </ModeToggle>

          {createMode === 'ai' ? (
            <Field>
              <label>Descrição do imóvel</label>
              <TextArea
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                placeholder={
                  'Ex.: Apartamento de 2 quartos, 1 suíte, 70m², com varanda gourmet no bairro ' +
                  'Jardim das Flores, São Paulo. Aceita financiamento. Valor R$ 450.000.'
                }
              />
              <Hint>
                A IA vai separar venda, locação, condomínio e IPTU e preencher também
                áreas, quartos, suítes, banheiros, vagas, endereço e características.
                O que não estiver explícito ficará em branco para revisão na próxima tela.
              </Hint>
            </Field>
          ) : (
            <>
              <Field>
                <label>Título *</label>
                <Input
                  value={mTitle}
                  onChange={(e) => setMTitle(e.target.value)}
                  placeholder="Ex.: Apartamento 2 quartos no Centro"
                />
              </Field>
              <Grid2>
                <Field>
                  <label>Tipo</label>
                  <SelectInput value={mType} onChange={(e) => setMType(e.target.value)}>
                    {PROPERTY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </SelectInput>
                </Field>
                <Field>
                  <label>Transação</label>
                  <SelectInput
                    value={mTransaction}
                    onChange={(e) => setMTransaction(e.target.value)}
                  >
                    <option value="venda">Venda</option>
                    <option value="aluguel">Aluguel</option>
                  </SelectInput>
                </Field>
              </Grid2>
              <Field>
                <label>Preço (R$)</label>
                <Input
                  type="number"
                  value={mPrice}
                  onChange={(e) => setMPrice(e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </Field>
              <Hint>Você completará os demais dados na tela de edição do imóvel.</Hint>
            </>
          )}

          <Actions>
            <Button $variant="secondary" onClick={onClose} disabled={creating}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating
                ? createMode === 'ai'
                  ? 'Criando com IA...'
                  : 'Criando...'
                : 'Criar imóvel e continuar'}
            </Button>
          </Actions>
        </>
      )}
    </Modal>
  );
};

export default AttachToPropertyModal;
